import { Component, type ErrorInfo, type ReactNode } from 'react';

import { BridgewayGlyph } from '@/components/BridgewayGlyph';
import { config } from '@/config/env';
import { logger } from '@/logger.ts';

type Props = { children: ReactNode };
type State = { hasError: boolean; error: Error | null };

export class ErrorBoundary extends Component<Props, State> {
	state: State = { hasError: false, error: null };

	static getDerivedStateFromError(error: Error): State {
		return { hasError: true, error };
	}

	componentDidCatch(error: Error, info: ErrorInfo): void {
		try {
			logger.error('ErrorBoundary caught render error', {
				error: error.message,
				stack: error.stack,
				componentStack: info.componentStack,
			});
		} catch {
			// Never let logging strand the fallback.
		}
	}

	private handleReload = (): void => {
		window.location.reload();
	};

	render(): ReactNode {
		if (!this.state.hasError) {
			return this.props.children;
		}

		return (
			<div className="flex h-full min-h-screen w-full items-center justify-center">
				<div className="flex max-w-md flex-col items-center gap-4 px-6 text-center">
					<BridgewayGlyph />
					<p className="font-adobe-clean-display text-[18px] leading-[26px] font-black">
						Something went wrong
					</p>
					<p className="text-[14px] leading-[20px] text-(--text-muted)">
						An unexpected error interrupted the app. Reloading should get you
						back on track.
					</p>
					<button
						type="button"
						onClick={this.handleReload}
						className="bg-beacon-gold cursor-pointer rounded-full px-5 py-2 text-[14px] font-medium text-black transition-opacity hover:opacity-90"
					>
						Reload
					</button>
					{config.ENV === 'local' && this.state.error && (
						<details className="mt-2 max-w-full text-left text-[12px] text-(--text-muted)">
							<summary className="cursor-pointer">Error details</summary>
							<pre className="mt-2 overflow-auto whitespace-pre-wrap">
								{this.state.error.message}
								{'\n\n'}
								{this.state.error.stack}
							</pre>
						</details>
					)}
				</div>
			</div>
		);
	}
}
